"use client";

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaAngleLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Divider,
  Grid,
  Paper,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Person as PersonIcon,
  ShoppingCart as CartIcon,
  Receipt as OrderIcon,
  Favorite as WishlistIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VerifiedUser as VerifiedIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  Apartment as ApartmentIcon,
  Public as CountryIcon,
  LocalPostOffice as PostalIcon,
  Event as EventIcon,
  MonetizationOn as PaymentHistoryIcon,
  Subscriptions as SubscriptionIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const primaryGreen = "#DC3545";
const lightGreen = "#ffe5e5";
const darkGreen = "#DC3545";

function Userview() {
  const { userId } = useParams();
  const [activeTab, setActiveTab] = useState("orders");
  const [userData, setUserData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carts, setCarts] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [paymentData, setPaymentData] = useState({ orders: [], carts: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        // Fetch user profile
        const profileRes = await getWithTokenAPI(
          token,
          `/user/find-particular?_id=${userId}`
        );
        setUserData(profileRes.data);

        // Fetch user orders
        const ordersRes = await getWithTokenAPI(
          token,
          `/order/find-all?buyer_id=${userId}`
        );
        setOrders(ordersRes.data?.orders || []);

        // Fetch user carts
        const cartsRes = await getWithTokenAPI(
          token,
          `/cart/find-all?user_id=${userId}`
        );
        setCarts(cartsRes.data?.Cart || []);

        // Fetch user wishlists
        const wishlistsRes = await getWithTokenAPI(
          token,
          `/wishlist/find-all?user_id=${userId}`
        );
        setWishlists(wishlistsRes.data?.wishlist || []);

        // Fetch user addresses
        const addressesRes = await getWithTokenAPI(
          token,
          `/address/find-all?user_id=${userId}`
        );
        setAddresses(addressesRes.data?.address || []);

        // Fetch user subscription data
        const subscriptionRes = await getWithTokenAPI(
          token,
          `/subscription/find-all?user_id=${userId}`
        );
        if (subscriptionRes.data?.subscriber?.length > 0) {
          setSubscriptionData(subscriptionRes.data.subscriber[0]);
        }

        // Fetch payment data (orders and carts with payment info)
        const paymentRes = await getWithTokenAPI(
          token,
          `/user/getPaymentDetails/${userId}`
        );
        setPaymentData({
          orders: paymentRes.data?.orders || [],
          carts: paymentRes.data?.carts || [],
        });
      } catch (error) {
        toast.error("Failed to fetch user data");
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress style={{ color: primaryGreen }} />
        <Typography sx={{ ml: 2 }}>Loading user data...</Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
      case "Delivered":
      case "Paid":
        return "success";
      case "Inactive":
      case "Failed":
        return "error";
      case "Shipped":
        return "info";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case "Paid":
        return <CheckCircleIcon sx={{ color: "#4caf50" }} />;
      case "Failed":
        return <ErrorIcon sx={{ color: "#f44336" }} />;
      case "Pending":
        return <PendingIcon sx={{ color: "#ff9800" }} />;
      default:
        return <CancelIcon sx={{ color: "#9e9e9e" }} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const calculatePaymentSummary = () => {
    const orderPayments = paymentData.orders || [];
    const cartPayments = paymentData.carts || [];

    const totalPaid = [
      ...orderPayments.filter((order) => order.payment_status === "Paid"),
      ...cartPayments.filter((cart) => cart.payment_status === "Paid"),
    ].reduce(
      (sum, item) => sum + (item.total_price || item.total_Price || 0),
      0
    );

    const totalPending = [
      ...orderPayments.filter((order) => order.payment_status === "Pending"),
      ...cartPayments.filter((cart) => cart.payment_status === "Pending"),
    ].reduce(
      (sum, item) => sum + (item.total_price || item.total_Price || 0),
      0
    );

    const totalFailed = [
      ...orderPayments.filter((order) => order.payment_status === "Failed"),
      ...cartPayments.filter((cart) => cart.payment_status === "Failed"),
    ].reduce(
      (sum, item) => sum + (item.total_price || item.total_Price || 0),
      0
    );

    return { totalPaid, totalPending, totalFailed };
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: darkGreen,
          marginBottom: "20px",
        }}
      >
        <FaAngleLeft />
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", width: "150px" }}
          onClick={() => {
            navigate("/backoffice/Usermanagement");
          }}
        >
          back
        </Typography>
      </Box>

      {/* User Profile Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: "8px",
          border: `1px solid ${lightGreen}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              alignItems: "center",
              mr: { md: 4 },
              mb: { xs: 3, md: 0 },
            }}
          >
            <Avatar
              src={userData?.profile_image}
              sx={{
                width: 120,
                height: 120,
                border: `3px solid ${primaryGreen}`,
                mb: 2,
              }}
              imgProps={{
                onError: (e) => {
                  e.target.src =
                    "https://placehold.co/150x150/cccccc/333333?text=No+Image";
                },
              }}
            />
            <Box sx={{ display: "flex", mt: 1, flexDirection: "column" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: darkGreen }}
              >
                {userData?.name || "User"}
              </Typography>
              <Box sx={{ display: "flex", mt: 1 }}>
                {userData?.otp_verify && (
                  <Chip
                    label="Verified"
                    size="small"
                    sx={{
                      backgroundColor: lightGreen,
                      color: darkGreen,
                      mr: 1,
                    }}
                    icon={
                      <VerifiedIcon
                        fontSize="small"
                        sx={{ color: primaryGreen }}
                      />
                    }
                  />
                )}
                <Chip
                  label={userData?.status || "N/A"}
                  color={getStatusColor(userData?.status)}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ flex: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Email:</strong>{" "}
                  {userData?.email ? (
                    <Link href={`mailto:${userData.email}`} underline="hover">
                      {userData.email}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PhoneIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Phone:</strong>{" "}
                  {userData?.phone_number ? (
                    <Link
                      href={`tel:${userData.phone_number}`}
                      underline="hover"
                    >
                      {userData.phone_number}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Role:</strong> {userData?.role || "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ArticleIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>User ID:</strong> {userData?._id || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarMonthIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Created:</strong>{" "}
                  {userData?.createdAt
                    ? new Date(userData.createdAt).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarMonthIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Updated:</strong>{" "}
                  {userData?.updatedAt
                    ? new Date(userData.updatedAt).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SubscriptionIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Subscription:</strong>{" "}
                  {subscriptionData?.paid_status || "N/A"}
                </Typography>
              </Box>
              {subscriptionData?.paid_status == "Paid" && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EventIcon
                      sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                    />
                    <Typography variant="body1">
                      <strong>Start Date:</strong>{" "}
                      {formatDate(subscriptionData.start_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EventIcon
                      sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                    />
                    <Typography variant="body1">
                      <strong>End Date:</strong>{" "}
                      {formatDate(subscriptionData.end_date)}
                    </Typography>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Tabs Navigation */}
      <Box
        sx={{
          width: "100%",
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          bgcolor: lightGreen,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          flexGrow: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="user details tabs"
          variant="fullWidth"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: primaryGreen,
              height: "4px",
              borderRadius: "4px 4px 0 0",
            },
            "& .MuiTab-root": {
              color: darkGreen,
              fontWeight: "bold",
              textTransform: "none",
              py: 1.5,
              "&.Mui-selected": {
                color: primaryGreen,
                backgroundColor: "white",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor: "rgba(0, 178, 7, 0.05)",
              },
            },
          }}
        >
          <Tab
            label="Orders"
            value="orders"
            icon={<OrderIcon />}
            iconPosition="start"
          />
          <Tab
            label="Carts"
            value="carts"
            icon={<CartIcon />}
            iconPosition="start"
          />
          <Tab
            label="Wishlists"
            value="wishlists"
            icon={<WishlistIcon />}
            iconPosition="start"
          />
          <Tab
            label="Addresses"
            value="addresses"
            icon={<LocationIcon />}
            iconPosition="start"
          />
          <Tab
            label="Payments"
            value="payments"
            icon={<PaymentHistoryIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ width: "100%", minHeight: "60vh" }}>
        {activeTab === "orders" && (
          <Box>
            {orders.length > 0 ? (
              <Grid container spacing={3}>
                {orders.map((order) => {
                  console.log(order)
                  const products = order.cart_id?.cart || [];
                  const cartPaymentStatus =
                    order.cart_id?.payment_status || "Pending";
                  const orderPaymentStatus = order.payment_status || "Pending";
                  const taxAmount = order.cart_id?.tax || 0;
                  const totalPrice = order.total_price || 0;
                  const subtotal = totalPrice - taxAmount;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={order._id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderTop: `4px solid ${primaryGreen}`,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                          },
                        }}
                      >
                        <CardHeader
                          title={`Order #${
                            order.order_id ||
                            order._id?.substring(0, 8) ||
                            "N/A"
                          }`}
                          subheader={formatDate(order.created_at)}
                          titleTypographyProps={{
                            variant: "subtitle1",
                            fontWeight: "bold",
                            color: darkGreen,
                          }}
                          action={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={order.order_status || "N/A"}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    order.order_status === "Delivered"
                                      ? lightGreen
                                      : order.order_status === "Shipped"
                                      ? "#e3f2fd"
                                      : order.order_status === "Pending"
                                      ? "#fff8e1"
                                      : "#f5f5f5",
                                  color:
                                    order.order_status === "Delivered"
                                      ? darkGreen
                                      : order.order_status === "Shipped"
                                      ? "#1565c0"
                                      : order.order_status === "Pending"
                                      ? "#ff8f00"
                                      : "#616161",
                                }}
                              />
                            </Box>
                          }
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <PersonIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              Customer: {order.buyer_id?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                              }}
                            >
                              <EmailIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              {order.buyer_id?.email || "N/A"}
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <ShippingIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              Shipping: {order.address_id?.city || "N/A"},{" "}
                              {order.address_id?.state || "N/A"}
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <PaymentIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: primaryGreen }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {order.payment_method || "N/A"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                               
                                {/* This is the active "Cart" Chip */}
                                <Chip
                                  label={`Cart: ${cartPaymentStatus}`}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      cartPaymentStatus === "Paid"
                                        ? lightGreen
                                        : cartPaymentStatus === "Failed"
                                        ? "#ffebee"
                                        : "#fff8e1",
                                    color:
                                      cartPaymentStatus === "Paid"
                                        ? darkGreen
                                        : cartPaymentStatus === "Failed"
                                        ? "#d32f2f"
                                        : "#ff8f00",
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, color: darkGreen, fontWeight: "bold" }}
                          >
                            Order Items ({products.length || 0})
                          </Typography>

                          {products.length > 0 ? (
                            <Box>
                              <Grid container spacing={1}>
                                {products.map((item, index) => (
                                  <Grid item xs={4} key={index}>
                                    <Box
                                      sx={{
                                        position: "relative",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Avatar
                                        src={
                                          item.product_id?.product_image?.[0]
                                        }
                                        variant="rounded"
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          border: `1px solid ${primaryGreen}`,
                                          margin: "0 auto",
                                        }}
                                      />
                                      <Badge
                                        badgeContent={item.quantity}
                                        sx={{
                                          "& .MuiBadge-badge": {
                                            backgroundColor: primaryGreen,
                                            color: "white",
                                          },
                                          position: "absolute",
                                          top: -8,
                                          right: -8,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          mt: 0.5,
                                          fontWeight: 600,
                                          color: darkGreen,
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 56,
                                        }}
                                      >
                                        {item.product_id?.name || "N/A"}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          color: "text.secondary",
                                        }}
                                      >
                                        (Ksh){item.discount_price || "N/A"}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                                {products.length > 3 && (
                                  <Grid item xs={4}>
                                    <Avatar
                                      sx={{
                                        width: 56,
                                        height: 56,
                                        bgcolor: lightGreen,
                                        color: darkGreen,
                                        border: `1px dashed ${primaryGreen}`,
                                        fontSize: "0.8rem",
                                      }}
                                    >
                                      +{products.length - 3}
                                    </Avatar>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              No items in this order
                            </Typography>
                          )}

                          {products.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              {products.map((item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 0.5,
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      maxWidth: "70%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.product_id?.name || "Product"} x
                                    {item.quantity}
                                  </Typography>
                                  <Typography variant="caption">
                                    (Ksh)
                                    {(
                                      item.discount_price * item.quantity
                                    ).toFixed(2)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>

                        <Box
                          sx={{
                            p: 2,
                            bgcolor: lightGreen,
                            borderBottomLeftRadius: "8px",
                            borderBottomRightRadius: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">Subtotal(Ksh):</Typography>
                            <Typography variant="body2">
                              {subtotal.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">Tax(Ksh):</Typography>
                            <Typography variant="body2">
                              {taxAmount.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "bold", color: darkGreen }}
                            >
                              Total(Ksh):
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "bold", color: darkGreen }}
                            >
                              {totalPrice.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No orders found for this User.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === "carts" && (
          <Box>
            {carts.length > 0 ? (
              <Grid container spacing={3}>
                {carts.map((cart) => (
                  <Grid item xs={12} sm={6} md={4} key={cart._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${primaryGreen}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      <CardHeader
                        title={`Cart #${cart._id?.substring(0, 8) || "N/A"}`}
                        subheader={`User: ${
                          cart.user_id?.name || cart.user_id?.email || "N/A"
                        }`}
                        titleTypographyProps={{
                          variant: "subtitle1",
                          fontWeight: "bold",
                          color: darkGreen,
                        }}
                        subheaderTypographyProps={{
                          variant: "body2",
                          color: "text.secondary",
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        {cart.cart?.map((item, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {index + 1}. Product:{" "}
                            {item.product_id?.name || "N/A"} — Quantity:{" "}
                            {item.quantity || 0}
                          </Typography>
                        ))}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Created At:{" "}
                          {new Date(cart.createdAt).toLocaleDateString() ||
                            "N/A"}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, color: darkGreen }}
                        >
                          Items ({cart.cart?.length || 0})
                        </Typography>
                        <Grid container spacing={1}>
                          {cart.cart?.map((item, index) => (
                            <Grid item xs={4} key={index}>
                              <Box sx={{ position: "relative" }}>
                                <Avatar
                                  src={item.product_id?.product_image?.[0]}
                                  variant="rounded"
                                  sx={{
                                    width: 56,
                                    height: 56,
                                    border: `1px solid ${primaryGreen}`,
                                  }}
                                  imgProps={{
                                    onError: (e) => {
                                      e.target.src =
                                        "https://placehold.co/50x50/cccccc/333333?text=Product";
                                    },
                                  }}
                                />
                                <Badge
                                  badgeContent={item.quantity}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      backgroundColor: primaryGreen,
                                      color: "white",
                                    },
                                    position: "absolute",
                                    top: -8,
                                    right: -8,
                                  }}
                                />
                              </Box>
                            </Grid>
                          ))}
                          {cart.cart?.length > 3 && (
                            <Grid item xs={4}>
                              <Avatar
                                sx={{
                                  width: 56,
                                  height: 56,
                                  bgcolor: lightGreen,
                                  color: darkGreen,
                                  border: `1px dashed ${primaryGreen}`,
                                  fontSize: "0.8rem",
                                }}
                              >
                                +{cart.cart.length - 3}
                              </Avatar>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: lightGreen,
                          textAlign: "right",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: darkGreen, fontWeight: "bold" }}
                        >
                          Total(Ksh): {cart.total_Price || "0"}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No carts found for this User.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === "wishlists" && (
          <Box>
            {wishlists.length > 0 ? (
              <Grid container spacing={3}>
                {wishlists.map((wishlist) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={wishlist._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${primaryGreen}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      <CardHeader
                        title={`Wishlist Item`}
                        subheader={`Added: ${new Date(
                          wishlist.createdAt
                        ).toLocaleDateString()}`}
                        titleTypographyProps={{
                          color: darkGreen,
                          fontWeight: "bold",
                        }}
                        avatar={
                          <Avatar
                            sx={{ bgcolor: primaryGreen, color: "white" }}
                          >
                            <WishlistIcon />
                          </Avatar>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <Avatar
                            src={wishlist.product_id?.product_image?.[0]}
                            variant="rounded"
                            sx={{
                              width: 120,
                              height: 120,
                              border: `2px solid ${primaryGreen}`,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{ mt: 2, color: darkGreen, fontWeight: "bold" }}
                        >
                          {wishlist.product_id?.name || "Unnamed Product"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mt: 1 }}
                        >
                          Original Price(Ksh): 
                          {wishlist.product_id?.original_price || "0"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          Discount Price(Ksh): 
                          {wishlist.product_id?.discount_price || "0"}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No wishlists found for this user.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === "addresses" && (
          <Box>
            {addresses.length > 0 ? (
              <Grid container spacing={3}>
                {addresses.map((address) => (
                  <Grid item xs={12} sm={6} md={4} key={address._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${primaryGreen}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      <CardHeader
                        title={address.name || "Address"}
                        subheader={address.address_type || "Default"}
                        titleTypographyProps={{
                          variant: "subtitle1",
                          fontWeight: "bold",
                          color: darkGreen,
                        }}
                        avatar={
                          <Avatar
                            sx={{ bgcolor: lightGreen, color: primaryGreen }}
                          >
                            {address.address_type === "Home" ? (
                              <HomeIcon />
                            ) : address.address_type === "Work" ? (
                              <ApartmentIcon />
                            ) : (
                              <LocationIcon />
                            )}
                          </Avatar>
                        }
                        action={
                          address.is_default && (
                            <Chip
                              label="Default"
                              size="small"
                              sx={{
                                backgroundColor: lightGreen,
                                color: darkGreen,
                                fontWeight: "bold",
                              }}
                            />
                          )
                        }
                      />
                      <CardContent>
                        <List dense>
                          <ListItem>
                            <ListItemIcon
                              sx={{ minWidth: 36, color: primaryGreen }}
                            >
                              <LocationIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={address.street}
                              secondary="Street"
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon
                              sx={{ minWidth: 36, color: primaryGreen }}
                            >
                              <LocationIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${address.city}, ${address.state}`}
                              secondary="City, State"
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon
                              sx={{ minWidth: 36, color: primaryGreen }}
                            >
                              <CountryIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={address.country}
                              secondary="Country"
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon
                              sx={{ minWidth: 36, color: primaryGreen }}
                            >
                              <PostalIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={address.zip_code}
                              secondary="Postal Code"
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No addresses found for this user.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === "payments" && (
          <Box>
            {/* Order Payments */}
            {paymentData.orders && paymentData.orders.length > 0 && (
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  borderTop: `4px solid ${primaryGreen}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <CardHeader
                  title="Order Payments"
                  titleTypographyProps={{
                    variant: "h6",
                    color: darkGreen,
                    fontWeight: "bold",
                  }}
                  avatar={
                    <Avatar sx={{ bgcolor: primaryGreen }}>
                      <OrderIcon />
                    </Avatar>
                  }
                />
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: lightGreen }}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Order ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Payment Method
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentData.orders.map((order, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {order.order_id ||
                                  `#${order._id?.substring(0, 8)}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(order.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrency(order.total_price)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <CreditCardIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: primaryGreen }}
                                />
                                <Typography variant="body2">
                                  {order.payment_method || "N/A"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {getPaymentStatusIcon(order.payment_status)}
                                <Chip
                                  label={order.payment_status || "N/A"}
                                  size="small"
                                  color={getStatusColor(order.payment_status)}
                                  sx={{ ml: 1 }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Cart Payments */}
            {paymentData.carts && paymentData.carts.length > 0 && (
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  borderTop: `4px solid ${primaryGreen}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <CardHeader
                  title="Cart Payments"
                  titleTypographyProps={{
                    variant: "h6",
                    color: darkGreen,
                    fontWeight: "bold",
                  }}
                  avatar={
                    <Avatar sx={{ bgcolor: primaryGreen }}>
                      <CartIcon />
                    </Avatar>
                  }
                />
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: lightGreen }}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Cart ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Payment Reference
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Receipt
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentData.carts.map((cart, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                #{cart._id?.substring(0, 8)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(cart.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrency(cart.total_Price)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {cart.payment_reference || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold" }}
                              >
                                {cart.payment_receipt || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {getPaymentStatusIcon(cart.payment_status)}
                                <Chip
                                  label={cart.payment_status || "N/A"}
                                  size="small"
                                  color={getStatusColor(cart.payment_status)}
                                  sx={{ ml: 1 }}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Subscription Payment Details */}
            {subscriptionData && (
              <Card
                variant="outlined"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  borderTop: `4px solid ${primaryGreen}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <CardHeader
                  title="Subscription Payment Details"
                  titleTypographyProps={{
                    variant: "h6",
                    color: darkGreen,
                    fontWeight: "bold",
                  }}
                  avatar={
                    <Avatar sx={{ bgcolor: primaryGreen }}>
                      <SubscriptionIcon />
                    </Avatar>
                  }
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Start Date:</strong>{" "}
                        {formatDate(subscriptionData.start_date)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>End Date:</strong>{" "}
                        {formatDate(subscriptionData.end_date)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Remaining Days:</strong>{" "}
                        {subscriptionData.remaining_days}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Amount:</strong>{" "}
                        {formatCurrency(subscriptionData.amount)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Tax:</strong>{" "}
                        {formatCurrency(subscriptionData.tax_amount)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total:</strong>{" "}
                        {formatCurrency(subscriptionData.total_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body1" sx={{ mr: 1 }}>
                          <strong>Payment Status:</strong>
                        </Typography>
                        {getPaymentStatusIcon(subscriptionData.paid_status)}
                        <Chip
                          label={subscriptionData.paid_status}
                          color={getStatusColor(subscriptionData.paid_status)}
                          size="medium"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* No Payment Data Message */}
            {(!paymentData.orders || paymentData.orders.length === 0) &&
              (!paymentData.carts || paymentData.carts.length === 0) &&
              !subscriptionData && (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: `1px dashed ${primaryGreen}`,
                    borderRadius: "8px",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: lightGreen,
                      color: primaryGreen,
                      mx: "auto",
                      mb: 2,
                      width: 64,
                      height: 64,
                    }}
                  >
                    <PaymentHistoryIcon fontSize="large" />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: darkGreen, mb: 1 }}>
                    No payment history found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This user hasn't made any payments yet.
                  </Typography>
                </Paper>
              )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Userview;
